import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import logoSite from '../assets/logo-site.png';
import { ThemeToggle } from '../components/ThemeToggle';

export function Login() {
    const { signed, signIn } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    if (signed) {
        return <Navigate to="/" />;
    }

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await signIn(email, password);
        } catch (err: any) {
            setError(err.message || 'Falha ao realizar login. Verifique suas credenciais.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-background-dark">
            {/* Toggle de tema no canto superior direito */}
            <div className="absolute top-6 right-6 z-20">
                <ThemeToggle />
            </div>

            {/* Ambient glows — mesmos do layout interno */}
            <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-primary/10 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-[-20%] right-[-10%] w-[40%] h-[50%] bg-slate-700/20 rounded-full blur-[150px] pointer-events-none" />

            {/* Logo */}
            <div className="relative z-10 mb-10 flex flex-col items-center animate-fade-in-up">
                <img src={logoSite} alt="Chalk Finance" className="h-12 object-contain mb-4" />
                <p className="text-sm font-medium text-slate-500">
                    Gerencie seu DRE e Fluxo de Caixa
                </p>
            </div>

            {/* Card — mesmo glass-panel das telas internas */}
            <div
                className="relative z-10 w-full max-w-[420px] animate-fade-in-up glass-panel rounded-2xl border border-slate-800/80 shadow-2xl"
                style={{ animationDelay: '0.1s' }}
            >
                <div className="px-8 py-10">
                    <h2 className="text-lg font-black text-white mb-8 tracking-tight">
                        Entrar na conta
                    </h2>

                    <form onSubmit={handleLogin} className="space-y-5">
                        {/* Email */}
                        <div>
                            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                                Email
                            </label>
                            <div className="relative group">
                                <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-600 text-[18px] group-focus-within:text-primary transition-colors z-10">
                                    mail
                                </span>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="appearance-none block w-full pl-10 pr-4 py-3 bg-slate-800/60 border border-slate-700/60 rounded-xl text-white placeholder-slate-600 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60 transition-all"
                                    placeholder="seu@email.com"
                                />
                            </div>
                        </div>

                        {/* Senha */}
                        <div>
                            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                                Senha
                            </label>
                            <div className="relative group">
                                <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-600 text-[18px] group-focus-within:text-primary transition-colors z-10">
                                    lock
                                </span>
                                <input
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="appearance-none block w-full pl-10 pr-4 py-3 bg-slate-800/60 border border-slate-700/60 rounded-xl text-white placeholder-slate-600 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60 transition-all"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        {/* Erro */}
                        {error && (
                            <div className="flex items-center gap-3 text-rose-400 text-sm font-medium bg-rose-500/10 border border-rose-500/20 p-3 rounded-xl animate-fade-in">
                                <span className="material-symbols-outlined text-[18px]">error</span>
                                <p>{error}</p>
                            </div>
                        )}

                        {/* Botão */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex justify-center items-center py-3.5 px-4 rounded-xl text-[14px] font-black uppercase tracking-wider text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg shadow-primary/20 hover:-translate-y-0.5 mt-2"
                        >
                            {isLoading ? (
                                <span className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                    Entrando...
                                </span>
                            ) : (
                                'Entrar na Plataforma'
                            )}
                        </button>

                        {/* Lembrar / Esqueceu */}
                        <div className="flex items-center justify-between pt-1">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-800 text-primary focus:ring-0"
                                />
                                <span className="text-xs text-slate-500 hover:text-slate-400 transition-colors">
                                    Lembrar de mim
                                </span>
                            </label>
                            <a href="#" className="text-xs text-slate-500 hover:text-primary transition-colors font-medium">
                                Esqueceu a senha?
                            </a>
                        </div>
                    </form>
                </div>
            </div>

            <p className="relative z-10 mt-10 text-xs text-slate-600">
                © 2026 Chalk Finance. Todos os direitos reservados.
            </p>
        </div>
    );
}
